import { Stack } from '@mui/material';

import { ImpactCo2AnswerStatsPublic } from '@/domain/impact-stats/entities/stats-public.entity';
import { computeStatsForThisTransportationMode } from '@/domain/impact-stats/utils/compute-stats-for-specific-case';
import { translate } from '@/ui/i18n';
import { ExtrapolationInput } from '@/ui/pages/app/components/quiz-panel/co2/components/public-answers/ExtrapolationInput';

import { BoxData } from './BoxData';
import { useExtrapolationTransport } from './hooks/useExtrapolationTransport';
import { TextData } from './TextData';

export function TransportData({
  statsPublic,
}: {
  statsPublic: ImpactCo2AnswerStatsPublic;
}) {
  const {
    extrapolationValue,
    setExtrapolationValue,
    statsPublicOverride,
    valueColor,
    isHighFactor,
  } = useExtrapolationTransport(statsPublic);

  return (
    <Stack sx={{ marginY: 2 }}>
      <Stack sx={{ flexDirection: 'row', justifyContent: 'flex-end', mb: 2 }}>
        <ExtrapolationInput
          value={extrapolationValue}
          onChange={setExtrapolationValue}
          personCount={statsPublic.publicMobilitySumTotalPassengerCount}
          showWarning={isHighFactor}
        />
      </Stack>
      <TransportTitle
        statsPublic={statsPublicOverride}
        valueColor={valueColor}
      />
      {statsPublicOverride.publicMobility.map((x) => (
        <TransportItem
          key={x.transportationMode}
          item={x}
          totalPassengerCount={
            statsPublicOverride.publicMobilitySumTotalPassengerCount
          }
          valueColor={valueColor}
        />
      ))}
    </Stack>
  );
}

function TransportTitle({
  statsPublic,
  valueColor,
}: {
  statsPublic: ImpactCo2AnswerStatsPublic;
  valueColor?: string;
}) {
  const totalMobilityScore = statsPublic.publicMobility.reduce(
    (sum, x) => sum + x.totalScore,
    0,
  );
  return (
    <TextData
      label={translate('Quiz.public-mobility-total-travel-distance')}
      value={translate('Quiz.km_cumulative', {
        count: statsPublic.publicMobilitySumTotalTravelDistance,
      })}
      scoreCo2={totalMobilityScore}
      valueColor={valueColor}
    />
  );
}

function TransportItem({
  item,
  totalPassengerCount,
  valueColor,
}: {
  item: ImpactCo2AnswerStatsPublic['publicMobility'][number];
  totalPassengerCount: number;
  valueColor?: string;
}) {
  const stats = computeStatsForThisTransportationMode(
    item,
    totalPassengerCount,
  );
  return (
    <BoxData
      title={translate(
        `ImpactQuestion.MOBILITY.transportationModeValues.${item.transportationMode}`,
      )}
      score={item.totalScore}
      lines={[
        translate('Projects.X-persons', { count: item.totalPassengerCount }),
        translate('Quiz.km_cumulative', { count: item.totalTravelDistance }),
      ]}
      footer={translate('Quiz.public-mobility-stats-sentence', {
        percentagePassenger: (stats.percentagePassenger * 100).toFixed(1),
        averageTravelDistance: stats.averageTravelDistance.toFixed(0),
      })}
      valueColor={valueColor}
    />
  );
}
